import React from 'react';
import { useAuthState } from 'react-admin';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { useTenantTerminology } from './useTenantTerminology';
import { useTenant } from '../contexts/TenantContext';
import { isGolfOSVariant } from '../config/branding';

// Icons
import PeopleIcon from '@mui/icons-material/People';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';

import BusinessIcon from '@mui/icons-material/Business';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReceiptIcon from '@mui/icons-material/Receipt';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import PaymentIcon from '@mui/icons-material/Payment';
import BuildIcon from '@mui/icons-material/Build';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import RouteIcon from '@mui/icons-material/Route';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import CategoryIcon from '@mui/icons-material/Category';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ChatIcon from '@mui/icons-material/Chat';
import GroupIcon from '@mui/icons-material/Group';
import ApartmentIcon from '@mui/icons-material/Apartment';
import StoreIcon from '@mui/icons-material/Store';
import GolfCourseIcon from '@mui/icons-material/GolfCourse';
import EventIcon from '@mui/icons-material/Event';
import HomeIcon from '@mui/icons-material/Home';
import FactCheckIcon from '@mui/icons-material/FactCheck';

export interface MenuItem {
  to: string;
  icon: React.ReactElement;
  text: string;
  path: string;
  featureFlag?: string;
  requiredPermissions?: string[];
}

export interface MenuSection {
  title: string;
  icon?: React.ReactElement;
  items: MenuItem[];
}

// Helper function to check nested feature flags
const checkFeatureFlag = (flags: any, flagPath: string): boolean => {
  const pathParts = flagPath.split('.');
  let current = flags;

  for (const part of pathParts) {
    if (current && typeof current === 'object' && part in current) {
      current = current[part];
    } else {
      return false;
    }
  }

  return current === true;
};

export const useMenuItems = () => {
  const { flags } = useFeatureFlags();
  const { identity } = useAuthState();
  const { t } = useTenantTerminology();
  const { currentTenant } = useTenant();

  const allMenuSections: MenuSection[] = [
    {
      title: t('menu.main', 'Main'),
      items: [
        {
          to: '/',
          icon: <DashboardIcon />,
          text: t('menu.dashboard', 'Dashboard'),
          path: '/',
        },
      ],
    },
    {
      title: t('menu.crm', 'CRM'),
      icon: <BusinessIcon fontSize="small" />,
      items: [
        {
          to: '/customers',
          icon: <BusinessIcon />,
          text: t('menu.customers', 'Customers'),
          path: '/customers',
          featureFlag: 'crm.customers',
        },
        {
          to: '/leads',
          icon: <PersonAddIcon />,
          text: t('menu.leads', 'Leads'),
          path: '/leads',
          featureFlag: 'crm.leads',
        },
        {
          to: '/opportunities',
          icon: <TrendingUpIcon />,
          text: t('menu.opportunities', 'Opportunities'),
          path: '/opportunities',
          featureFlag: 'crm.opportunities',
        },
      ],
    },
    {
      title: t('menu.sales', 'Sales'),
      icon: <ReceiptIcon fontSize="small" />,
      items: [
        {
          to: '/estimates',
          icon: <RequestQuoteIcon />,
          text: t('menu.estimates', 'Estimates'),
          path: '/estimates',
          featureFlag: 'sales.estimates',
        },
        {
          to: '/invoices',
          icon: <ReceiptIcon />,
          text: t('menu.invoices', 'Invoices'),
          path: '/invoices',
          featureFlag: 'sales.invoices',
        },
        {
          to: '/crm/payments',
          icon: <PaymentIcon />,
          text: t('menu.payments', 'Payments'),
          path: '/crm/payments',
          featureFlag: 'sales.payments',
        },
      ],
    },
    {
      title: t('menu.golfBooking', 'GOLF & AMENITY BOOKING'),
      icon: <GolfCourseIcon fontSize="small" />,
      items: [
        ...(!isGolfOSVariant
          ? [
              {
                to: '/tee-time-management',
                icon: <GolfCourseIcon />,
                text: t('menu.teeTimeManagement', 'Tee Time Management'),
                path: '/tee-time-management',
              },
            ]
          : []),
        ...(!isGolfOSVariant
          ? [
              {
                to: '/course-services',
                icon: <CalendarTodayIcon />,
                text: t('menu.courseServices', 'Course Services'),
                path: '/course-services',
              },
            ]
          : []),
        ...(!isGolfOSVariant
          ? [
              {
                to: '/facility-reservations',
                icon: <HomeIcon />,
                text: t('menu.facilityReservations', 'Facility Reservations'),
                path: '/facility-reservations',
              },
            ]
          : []),
      ],
    },
    {
      title: t('menu.operations', 'Operations'),
      icon: <BuildIcon fontSize="small" />,
      items: [
        {
          to: '/tasks',
          icon: <AssignmentIcon />,
          text: t('menu.tasks', 'Tasks'),
          path: '/tasks',
        },
        {
          to: '/calendar',
          icon: <EventIcon />,
          text: t('menu.schedule', 'Schedule'),
          path: '/calendar',
          featureFlag: 'operations.calendar',
        },
        {
          to: '/forms',
          icon: <FactCheckIcon />,
          text: t('menu.completedForms', 'Completed Forms'),
          path: '/forms',
          featureFlag: 'forms',
        },
        {
          to: '/chat',
          icon: <ChatIcon />,
          text: t('menu.chat', 'Chat'),
          path: '/chat',
          featureFlag: '',
        },
        {
          to: '/routes',
          icon: <RouteIcon />,
          text: t('menu.routes', 'Routes'),
          path: '/routes',
          featureFlag: 'operations.routes',
        },
      ],
    },
    {
      title: t('menu.assets', 'Assets'),
      icon: <BusinessCenterIcon fontSize="small" />,
      items: [
        {
          to: '/sites',
          icon: <LocationOnIcon />,
          text: t('menu.sites', 'Sites'),
          path: '/sites',
        },
        {
          to: '/equipment',
          icon: <LocalShippingIcon />,
          text: t('menu.vehicles', 'Vehicles'),
          path: '/equipment',
          featureFlag: 'assets.vehicles',
        },
        {
          to: '/residential-units',
          icon: <PeopleIcon />,
          text: t('menu.drivers', 'Drivers'),
          path: '/residential-units',
          featureFlag: 'assets.drivers',
        },
        {
          to: '/users',
          icon: <PeopleIcon />,
          text: t('menu.users', 'Users'),
          path: '/users',
          featureFlag: '',
        },
      ],
    },
    {
      title: t('menu.franchise', 'Franchise'),
      icon: <ApartmentIcon fontSize="small" />,
      items: [
        {
          to: '/enterprises',
          icon: <ApartmentIcon />,
          text: t('menu.enterprises', 'Enterprises'),
          path: '/enterprises',
        },
        {
          to: '/brands',
          icon: <StoreIcon />,
          text: t('menu.brands', 'Brands'),
          path: '/brands',
        },
        {
          to: '/regions',
          icon: <LocationOnIcon />,
          text: t('menu.regions', 'Regions'),
          path: '/regions',
        },
      ],
    },
    {
      title: t('menu.configuration', 'Configuration'),
      icon: <SettingsIcon fontSize="small" />,
      items: [
        {
          to: '/configuration',
          icon: <SettingsIcon />,
          text: t('menu.configuration', 'Configuration'),
          path: '/configuration',
          featureFlag: 'configuration.settings',
        },
        {
          to: '/users',
          icon: <GroupIcon />,
          text: t('menu.users', 'Users'),
          path: '/users',
          requiredPermissions: ['admin'],
        },
      ],
    },
  ];

  // Filter menu items based on variant, feature flags, and permissions
  const filteredMenuSections = allMenuSections
    .map(section => {
      // Franchise section is only for enterprise admins (separate menu)
      if (section.title === 'Franchise') {
        return null;
      }

      // Golf & Amenity Booking: show for GolfOS variant or golf tenants on Distill
      const golfBookingTitle = t('menu.golfBooking', 'GOLF & AMENITY BOOKING');
      if (section.title === golfBookingTitle) {
        const tenantCode = currentTenant?.code?.toLowerCase() ?? '';
        const showGolfSection = isGolfOSVariant || ['dg', 'magna'].includes(tenantCode);
        if (!showGolfSection) return null;
      }

      // GolfOS variant: hide CRM and Sales sections
      if (isGolfOSVariant) {
        const crmTitle = t('menu.crm', 'CRM');
        const salesTitle = t('menu.sales', 'Sales');
        if (section.title === crmTitle || section.title === salesTitle) {
          return null;
        }
      }

      // Filter items based on feature flags and permissions
      const filteredItems = section.items.filter(item => {
        // Check permissions first
        if (item.requiredPermissions && item.requiredPermissions.length > 0) {
          const userRoles = (identity as any)?.roles || [];
          const hasPermission = item.requiredPermissions.some(permission => userRoles.includes(permission));
          if (!hasPermission) {
            return false;
          }
        }

        // If no feature flag is specified, show the item (permissions already checked)
        if (!item.featureFlag) {
          return true;
        }
        // Check if the nested feature flag is enabled
        return checkFeatureFlag(flags, item.featureFlag);
      });

      // Return section with filtered items, or null if no items
      return filteredItems.length > 0 ? { ...section, items: filteredItems } : null;
    })
    .filter((section): section is MenuSection => section !== null); // Remove null sections

  return filteredMenuSections;
};
